import { computed, effect, inject, Signal, signal, untracked, Service } from '@angular/core'
import { HttpErrorResponse, httpResource } from '@angular/common/http'
import { Article } from '../models/article'
import { Page } from '../models/page'
import { AppState } from '../models/app-state'
import { StorageService } from './storage.service'
import { TranslateService } from '@ngx-translate/core'
import { LanguageService } from './language.service'
import { environment } from '../../environments/environment'
import { MedicalTrail } from '../models/medical-trail'
import { captureException } from '../helpers/error-capture.helper'
import { resolveAppApiUrl } from '../helpers/app-api-url.helper'
import initialArticleData from '../initial-data/article.data'

const APP_STATE_KEY = 'app-state-v2'
const DEFAULT_TREATMENT_PLACE = 'andet'
const APP_CONTENT_SYNC_FAILED_TAG = 'app-content-sync-failed'
const APP_CONTENT_SYNC_TRANSIENT_TAG = 'app-content-sync-transient'

function isTransientAppContentSyncError(error: unknown): error is HttpErrorResponse {
  return error instanceof HttpErrorResponse && error.status === 0
}

function createTransientAppContentSyncError(error: HttpErrorResponse): Error {
  return new Error(`Transient app content sync network failure for ${error.url ?? resolveAppApiUrl()}`, {
    cause: error,
  })
}

@Service()
export class StateService {
  private storageService = inject(StorageService)
  private translate = inject(TranslateService)
  private languageService = inject(LanguageService)

  #firstRun = true
  readonly #initialized = signal(false)
  constructor() {
    // skip the first initialization of the state - after that always save the state to storage
    effect(() => {
      if (this.#firstRun && this.#state()) {
        this.#firstRun = false
        return
      }
      this.storageService.set(APP_STATE_KEY, this.#state()).catch((error) => {
        // Log or capture the error to prevent unhandled rejection
        captureException(error, 'info', 'failed-to-save-state')
      })
    })
    // set the language for the translation service
    effect(() => {
      const language = this.currentLanguage()
      if (language) {
        this.translate.use(language)
      }
    })
    effect(() => {
      if (environment.env === 'development') {
        // log the state on updates
        console.log(this.#state()) // eslint-disable-line no-console
      }
    })
    // sync app content from httpResource to state when loaded
    effect(() => {
      const status = this.appContentResource.status()

      if (status === 'idle') {
        return
      }

      if (status === 'error') {
        const error = this.appContentResource.error()

        if (isTransientAppContentSyncError(error)) {
          captureException(createTransientAppContentSyncError(error), 'info', APP_CONTENT_SYNC_TRANSIENT_TAG)
          return
        }

        captureException(error ?? new Error('Failed to sync app content'), 'warning', APP_CONTENT_SYNC_FAILED_TAG)
        return
      }

      if (status !== 'resolved') {
        return
      }

      const appContent = this.appContentResource.value()
      const currentState = untracked(() => this.#state())

      this.appContentResource.destroy()
      this.setState({ ...currentState, ...appContent })
    })
  }

  #fromStorage = false

  async init() {
    try {
      const storageState = await this.storageService.get(APP_STATE_KEY)
      if (storageState) {
        this.#fromStorage = true
        this.setState(storageState)
      } else {
        this.#fromStorage = false
      }
    } catch (error) {
      this.#fromStorage = false
      captureException(error, 'warning', 'failed-to-load-state')
    } finally {
      this.#initialized.set(true)
    }
  }

  isStateFromStorage(): boolean {
    return this.#fromStorage
  }

  readonly #state = signal<AppState>({
    ...initialArticleData,
    treatmentPlace: DEFAULT_TREATMENT_PLACE,
    currentLanguage: this.languageService.getSystemLanguage(),
    medicalTrail: 'not-enrolled',
  })

  // HTTP resource for fetching app content
  private appContentResource = httpResource<Omit<AppState, 'treatmentPlace' | 'medicalTrail' | 'currentLanguage'>>(
    () => {
      if (!this.#initialized()) {
        return undefined
      }
      return {
        url: resolveAppApiUrl(),
        params: { v: '2' },
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
        },
      }
    },
  )

  state = this.#state.asReadonly()
  readonly articles: Signal<Array<Article>> = computed(() =>
    this.#state().articles.filter(
      (article) =>
        this.currentLanguage() === article.language &&
        (this.targetGroup() ? (article.targetGroup ?? 'pre-existing') === this.targetGroup() : true),
    ),
  )
  readonly allArticlesSorted: Signal<Array<Article>> = computed(() =>
    [...this.articles()].sort((a, b) => a.order - b.order),
  )
  readonly pages: Signal<Array<Page>> = computed(() =>
    this.#state().pages.filter((page) => this.currentLanguage() === page.language),
  )
  readonly frontPageIntro: Signal<string> = computed(
    () =>
      this.#state().frontPageIntros.find((frontPageIntro) => this.currentLanguage() === frontPageIntro.language)
        ?.content ?? '',
  )
  readonly currentLanguage: Signal<string | undefined> = computed(() => this.#state().currentLanguage)
  readonly currentTreatmentPlace: Signal<string | undefined> = computed(() => this.#state().treatmentPlace)
  readonly medicalTrail: Signal<MedicalTrail | undefined> = computed(() => this.#state().medicalTrail)
  readonly targetGroup: Signal<'pre-existing' | 'gestational' | undefined> = computed(() => this.#state().targetGroup)

  setState(state: AppState | undefined): void {
    if (!state) {
      return
    }

    const newestLastUpdated = this.getLatestArticleUpdate(state.articles)
    const olderLastUpdated = this.getLatestArticleUpdate(this.#state().articles)

    if (newestLastUpdated > olderLastUpdated) {
      this.#state.update((prev) => ({
        ...prev,
        ...state,
      }))
    } else {
      this.#state.update((prev) => ({
        ...prev,
        treatmentPlace: state.treatmentPlace ?? prev.treatmentPlace,
        currentLanguage: state.currentLanguage ?? prev.currentLanguage,
        medicalTrail: state.medicalTrail ?? prev.medicalTrail,
        targetGroup: state.targetGroup ?? prev.targetGroup,
      }))
    }
  }

  private getLatestArticleUpdate(articles: Article[]): number {
    // A single missing or invalid lastupdated (NaN, undefined) must not poison the
    // freshness comparison: it would block every future content merge otherwise.
    return articles.reduce(
      (latest, article) => (Number.isFinite(article.lastupdated) ? Math.max(latest, article.lastupdated) : latest),
      0,
    )
  }

  setCurrentLanguage(currentLanguage: string): void {
    this.#state.update((prev) => ({ ...prev, currentLanguage }))
  }

  setTargetGroup(targetGroup: 'pre-existing' | 'gestational'): void {
    this.#state.update((prev) => ({ ...prev, targetGroup }))
  }

  setCurrentTreatmentPlace(treatmentPlace: string): void {
    this.#state.update((prev) => ({ ...prev, treatmentPlace }))
  }

  getArticle(slug: string): Article | undefined {
    return this.#state().articles.find((article) => article.slug === slug)
  }

  getExtraPage(slug: string): Page | undefined {
    return this.#state().pages.find((page) => page.slug === slug)
  }

  getExtraPageForLanguage(slug: string, language: string): Page | undefined {
    return this.#state().pages.find((page) => page.slug === slug && page.language === language)
  }

  getFrontPageIntro(language: string): string | undefined {
    return this.#state().frontPageIntros.find((intro) => intro.language === language)?.content
  }
}
